import { HttpClient, HttpEventType } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, switchMap, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MainService } from '../../../../app/shared/services/main.service';
import { environment } from '../../../../environments/environment';

/**
 * Interface for password change request
 */
export interface ChangePasswordRequest {
    current_password: string;
    password: string;
    password_confirmation: string;
}

/**
 * Interface for password change response
 */
export interface ChangePasswordResponse {
    message: string;
    success: boolean;
}

/**
 * Interface for setting creation request
 */
export interface CreateSettingRequest {
    key: string;
    value: string;
}

/**
 * Interface for setting update request
 */
export interface UpdateSettingRequest {
    value: string;
}

/**
 * Interface for setting response
 */
export interface SettingResponse {
    id: string;
    key: string;
    value: string;
    created_at: string;
    updated_at: string;
}

export interface CommunicationChannel {
    id: number;
    name: string;
    is_default: boolean;
    is_active: boolean;
}

export interface BotSettingsResponse {
    whatsapp_enabled: boolean;
    telegram_enabled: boolean;
    telegram_custom_enabled: boolean;
    telegram_custom_token: string;
    webchat_enabled: boolean;
    permission_trip_request: boolean;
}

export type BotSettingsRequest = BotSettingsResponse;

export interface SchedulingSettingsResponse {
    warnings_enabled: boolean;
    warning_window_hours: number;
    warning_scope: 'aircraft' | 'crew';
}

export type SchedulingSettingsRequest = SchedulingSettingsResponse;

export interface AviationDataSettings {
    foreflight_enabled: boolean;
    aviapages_enabled: boolean;
    rapidapi_enabled: boolean;
}

export interface WeatherDataSettings {
    open_meteo_enabled: boolean;
    aviation_weather_enabled: boolean;
}

export interface ExternalConnectionsResponse {
    aviation_data: AviationDataSettings;
    weather_data?: WeatherDataSettings;
}

export interface ExternalConnectionsRequest {
    aviation_data: AviationDataSettings;
}

export interface FuelVendor {
    id: number;
    name: string;
    importer_id: number;
    provider_email: string;
    last_imported_at: Date | null;
    status: number | null;
    created_at: Date;
    updated_at: Date;
}

export interface ImportRequest {
    importer_id: number;
    format: 'csv';
    name: string;
    filename: string;
    params: {
        fuel_vendor_id: number;
    };
}

export interface FuelVendorsResponse {
    data: FuelVendor[];
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
    prev: null;
    next: null;
}

export interface FuelImportResponse {
    id: number;
    importer_id: number;
    filename: string;
    format: string;
    name: string;
    params: {
        fuel_vendor_id: number;
    };
    status: number;
    modified_by: number;
    created_at: Date;
    created_by: number;
}

/**
 * SettingsService - Handles security-related operations
 *
 * Provides methods for password management and security settings
 * using Angular 19 patterns with inject() function and typed interfaces
 */
@Injectable({
    providedIn: 'root',
})
export class SettingsService {
    private _httpClient = inject(HttpClient);
    private _mainService = inject(MainService);
    private readonly _baseUrl = `${environment.apiUrl}/api/v1`;

    /**
     * Changes user password
     * @param passwordData - Current and new password information
     * @returns Observable with change password response
     */
    changePassword(passwordData: ChangePasswordRequest): Observable<ChangePasswordResponse> {
        // console.log('Payload sent:', passwordData);
        return this._httpClient.post<ChangePasswordResponse>(`${this._baseUrl}/changePassword`, passwordData);
    }

    /**
     * Creates a new setting
     * @param settingData - Setting key and value
     * @returns Observable with setting response
     */
    createSetting(settingData: CreateSettingRequest): Observable<SettingResponse> {
        return this._httpClient.post<SettingResponse>(`${this._baseUrl}/settings`, settingData);
    }

    /**
     * Updates an existing setting
     * @param settingId - Setting ID to update
     * @param settingData - New setting value
     * @returns Observable with setting response
     */
    updateSetting(settingId: string, settingData: UpdateSettingRequest): Observable<SettingResponse> {
        return this._httpClient.put<SettingResponse>(`${this._baseUrl}/settings/${settingId}`, settingData);
    }

    /**
     * Gets a setting by ID
     * @param settingId - Setting ID to fetch
     * @returns Observable with setting response
     */
    getSettingById(settingId: string): Observable<SettingResponse> {
        return this._httpClient.get<SettingResponse>(`${this._baseUrl}/settings/${settingId}`);
    }

    /**
     * Gets a setting by KEY
     * @param settingKey - Setting KEY to fetch
     * @returns Observable with setting response
     */
    getSettingByKey(settingKey: string): Observable<SettingResponse> {
        return this._httpClient.get<SettingResponse>(`${this._baseUrl}/settings/key/${settingKey}`);
    }

    private _webchatEnabled$ = new BehaviorSubject<boolean>(false);
    webchatEnabled$ = this._webchatEnabled$.asObservable();

    getBotSettings(): Observable<BotSettingsResponse> {
        return this._httpClient.get<BotSettingsResponse>(`${this._baseUrl}/settings/bot`).pipe(
            tap((settings) => this._webchatEnabled$.next(settings.webchat_enabled ?? false)),
        );
    }

    updateBotSettings(data: BotSettingsRequest): Observable<BotSettingsResponse> {
        return this._httpClient.put<BotSettingsResponse>(`${this._baseUrl}/settings/bot`, data).pipe(
            tap((settings) => this._webchatEnabled$.next(settings.webchat_enabled ?? false)),
        );
    }

    getWebchatEnabled(): Observable<{ webchat_enabled: boolean }> {
        return this._httpClient.get<{ webchat_enabled: boolean }>(`${this._baseUrl}/settings/webchat-enabled`).pipe(
            tap((settings) => this._webchatEnabled$.next(settings.webchat_enabled ?? false)),
        );
    }

    getSchedulingSettings(): Observable<SchedulingSettingsResponse> {
        return this._httpClient.get<SchedulingSettingsResponse>(`${this._baseUrl}/settings/scheduling-warning-window`);
    }

    updateSchedulingSettings(data: SchedulingSettingsRequest): Observable<SchedulingSettingsResponse> {
        return this._httpClient.put<SchedulingSettingsResponse>(`${this._baseUrl}/settings/scheduling-warning-window`, data);
    }

    getExternalConnectionsSettings(): Observable<ExternalConnectionsResponse> {
        return this._httpClient.get<ExternalConnectionsResponse>(`${this._baseUrl}/settings/external-connections`);
    }

    updateExternalConnectionsSettings(data: ExternalConnectionsRequest): Observable<ExternalConnectionsResponse> {
        return this._httpClient.put<ExternalConnectionsResponse>(`${this._baseUrl}/settings/external-connections`, data);
    }

    getUserCommunicationChannels(): Observable<CommunicationChannel[]> {
        return this._httpClient.get<CommunicationChannel[]>(`${this._baseUrl}/user/communicationChannels`);
    }

    syncUserCommunicationChannels(channelIds: number[]): Observable<{ message: string }> {
        return this._httpClient.put<{ message: string }>(`${this._baseUrl}/user/communicationChannels`, { channel_ids: channelIds });
    }

    getFuelVendors(): any {
        return this._httpClient.get(`${this._baseUrl}/fuelVendors`);
    }

    getFuelVendor(vendorId: number): Observable<FuelVendor> {
        return this._httpClient.get<FuelVendor>(`${this._baseUrl}/fuelVendors/${vendorId}`);
    }

    uploadPricelistFile(vendorId: number, file: File): Observable<{ filePath: string; vendor: FuelVendor }> {
        return this.getFuelVendor(vendorId).pipe(
            switchMap((vendor) => {
                const vendorName = vendor.name.replace(/\s+/g, '-').toLowerCase();
                const fileExtension = file.name.split('.').pop();
                const filePath = `documents/fuel-vendors/${vendorName}/${uuidv4()}.${fileExtension}`;

                return this._mainService.getPresignedUrl(filePath).pipe(
                    switchMap((response) => this._mainService.uploadToS3(response.url, file)),
                    filter((event) => event.type === HttpEventType.Response && event.ok),
                    switchMap(() => [{ filePath, vendor }])
                );
            })
        );
    }
}
